import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListColumnType, ListFilterType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { useSettings } from 'services/settings/SettingsContext';
import { renderPriceDecimalColumn } from 'components/lists/functions/renderListColumn';
import InvoiceTemplateModal from './InvoiceTemplateModal';
import { buildScheduleSentence, renderScheduleStatusBadge } from './InvoiceScheduleCard';

// Set the filters of the periodic invoice list
const invoiceTemplateFilters: ListFilterType[] = [
    {
        id: 'all',
        label: 'invoice.template.all_filter_label',
        params: { 'deleted': false },
        default: true,
    },
    {
        group: 'invoice.general.status_filter_group_label',
        filters: [
            {
                id: 'active',
                label: 'invoice.schedule.status_active',
                params: { 'deleted': false, 'is_active': true },
            },
            {
                id: 'stopped',
                label: 'invoice.schedule.status_stopped',
                params: { 'deleted': false, 'is_active': false },
            },
        ]
    },
    {
        group: 'general.archive',
        filters: [
            {
                id: 'deleted',
                label: 'invoice.template.deleted_filter_label',
                shortName: 'general.deleted',
                params: { 'deleted': true },
            }
        ]
    }
];

// Renders the rhythm of a periodic invoice as one sentence
export const renderScheduleSentence = (row: any, t: (key: string, options?: any) => string, userLocale: string) => (
    <span>{row.schedule ? buildScheduleSentence(row.schedule, t, userLocale) : '-'}</span>
);

// Rename the name of the function:
const InvoiceTemplateList: React.FC = () => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();

    // Specify the options of this list:
    const query = {
        endpoint: 'get_invoice_template_list',
        defaultParams: { 'deleted': false },
        object: 'invoice_template'
    }
    const objectName = 'invoice.template.object_name'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = true

    // Specify form, detail modal or detailpage for this list
    const postNewItem = { url: 'post_invoice_template' }
    const detailModal = <InvoiceTemplateModal />;
    const onRowClick = 'detailModal'

    // Specify the columns of this list, the flexible tracks are content independent so the header and rows stay aligned
    const columns: ListColumnType[] = [
        {
            width: '1fr',
            field: 'number',
            label: 'invoice.general.invoice_number_label',
            render: (_, row) => <span>{row.number ? t('invoice.template.number_label', { number: row.number }) : '-'}</span>
        },
        {
            width: 'minmax(0, 3fr)',
            field: 'recipient_name',
            orderField: 'recipient__name',
            label: 'invoice.general.recipient_label'
        },
        {
            width: 'minmax(0, 3fr)',
            field: 'internal_reference',
            label: 'invoice.general.internal_reference_label'
        },
        {
            width: 'minmax(0, 4fr)',
            field: 'schedule',
            label: 'invoice.schedule.card_label',
            disableOrdering: true,
            render: (_, row) => renderScheduleSentence(row, t, userLocale)
        },
        {
            width: 'minmax(0, 1.5fr)',
            field: 'amount',
            label: 'invoice.general.amount_label',
            render: (_, row) => renderPriceDecimalColumn(row.amount, row.currency, userLocale)
        },
        {
            width: '144px',
            field: 'status',
            label: 'invoice.general.status_label',
            disableOrdering: true,
            render: (_, row) => renderScheduleStatusBadge(!!row.schedule?.is_active, t)
        }
    ];

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            filters={invoiceTemplateFilters}
            showSearch={true}
            postNewItem={postNewItem}
            detailModal={detailModal}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
        />
    );
};

export default InvoiceTemplateList;
